import React from "react";
import { track } from "../../utils/analytics";

function primaryDefinition(w) {
  return (
    w.senses?.find((s) => s.is_primary)?.definition ||
    w.conjugation_info?.definition ||
    w.senses?.[0]?.definition ||
    ""
  );
}

function WordCard({ word, onSelect, onPlay, isPlaying }) {
  const definition = primaryDefinition(word);
  return (
    <div
      className="surface-glass rounded-xl px-3 py-2.5 flex items-center gap-2 cursor-pointer hover:brightness-110 transition-[filter]"
      onClick={() => onSelect(word)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") onSelect(word); }}
    >
      <div className="flex-1 min-w-0">
        <p className="font-arabic text-xl font-semibold leading-snug" style={{ color: "var(--gold-bright)" }} dir="rtl">
          {word.text}
        </p>
        {word.transliteration && (
          <p className="text-xs italic truncate" style={{ color: "var(--parchment-dim)" }}>{word.transliteration}</p>
        )}
        {definition && (
          <p className="text-sm truncate" style={{ color: "var(--parchment)" }}>{definition}</p>
        )}
      </div>
      <button
        type="button"
        title={isPlaying ? "Stop" : "Play"}
        aria-label={isPlaying ? "Stop" : `Play ${word.text}`}
        onClick={(e) => { e.stopPropagation(); onPlay(word); }}
        className="shrink-0 p-2 rounded-full text-dt-parchment-dim hover:text-dt-gold-bright hover:bg-dt-forest-soft transition-colors"
      >
        {isPlaying ? (
          <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
            <rect x="5" y="5" width="14" height="14" rx="2" />
          </svg>
        ) : (
          <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
            <path d="M6 4l14 8-14 8V4z" />
          </svg>
        )}
      </button>
    </div>
  );
}

/**
 * PublicVocabList — the Vocabulary step on the shared-topic page.
 *
 * Word list only: browsable cards (tap → word detail, play → TTS) grouped
 * Words / Verbs / Expressions with the same rules the app's lists use. The
 * interactive learn-by-quizzing flow needs an account, so a sign-in CTA
 * stands in for it.
 */
export default function PublicVocabList({ words, onSelectWord, onPlayWord, playingWordId }) {
  // Same grouping rules as TopicDetail: hide show_in_list=false entries,
  // exclude conjugated forms whose root verb is already listed.
  const isInList = (w) => w.show_in_list !== false;
  const vocabWords = words.filter((w) => (w.word_type === "vocab" || !w.word_type) && !w.conjugation_info && isInList(w));
  const allVerbWords = words.filter((w) => w.word_type === "verb" && isInList(w));
  const rootVerbIds = new Set(allVerbWords.map((w) => w.id));
  const verbWords = allVerbWords.filter(
    (w) => !w.conjugation_info || !rootVerbIds.has(w.conjugation_info.word_id)
  );
  const expressionWords = words.filter((w) => w.word_type === "expression" && isInList(w));

  const sections = [
    { title: "Words", items: vocabWords },
    { title: "Verbs", items: verbWords },
    { title: "Expressions", items: expressionWords },
  ].filter((s) => s.items.length > 0);

  return (
    <div className="dark-scope space-y-5">
      <div
        className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 rounded-xl px-4 py-3"
        style={{ background: "var(--forest-soft)", border: "1px solid var(--panel-line)" }}
      >
        <p className="text-sm" style={{ color: "var(--parchment-dim)" }}>
          Tap a word to see its full detail, or press play to hear it. Sign up to quiz
          yourself on these words and track what you've learnt.
        </p>
        <a
          href="/?signup=1"
          onClick={() => track("share-topic-signup-click", { step: "vocabulary" })}
          className="shrink-0 px-3 py-1.5 rounded-lg bg-dt-gold text-dt-ink hover:brightness-105 text-xs font-semibold transition-[filter] no-underline text-center"
        >
          Start free trial
        </a>
      </div>

      {sections.length === 0 && (
        <p className="text-sm" style={{ color: "var(--parchment-dim)" }}>No vocabulary listed for this topic.</p>
      )}

      {sections.map((section) => (
        <section key={section.title}>
          <h3 className="text-xs font-semibold uppercase tracking-wide mb-2 text-dt-gold-dim">
            {section.title}
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {section.items.map((w) => (
              <WordCard
                key={w.id}
                word={w}
                onSelect={onSelectWord}
                onPlay={onPlayWord}
                isPlaying={playingWordId === w.id}
              />
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}
